import httpClient from "./httpClient";
import qs from "qs";
import type {
  ApiResponse,
  LoginCredentials,
  UpdateParametersPayload,
  UpdateProfilePayload,
} from "../types/api";

export default class SessionApi {
  static login(credentials: LoginCredentials): Promise<ApiResponse> {
    return httpClient
      .post(
        API_URL + "/authentication/login",
        qs.stringify({
          username: credentials.username,
          password: credentials.password,
        })
      )
      .then((response) => response.data);
  }

  static use(customerId: number | string): Promise<ApiResponse> {
    return httpClient
      .post(
        API_URL + "/authentication/use",
        qs.stringify({
          customer_id: customerId,
        })
      )
      .then((response) => response.data);
  }

  static switchCustomer(customerId: number | string): Promise<ApiResponse> {
    return this.use(customerId);
  }

  static borrowCustomer(
    customerId: number | string,
    adminToken: string | null = null
  ): Promise<ApiResponse> {
    return httpClient
      .post(
        API_URL + "/authentication/borrow_customer",
        qs.stringify({
          customer_id: customerId,
          admin_token: adminToken,
        })
      )
      .then((response) => response.data);
  }

  static checkAdminToken(token: string | null = null): Promise<ApiResponse> {
    const payload = typeof token === "string" && token ? { admin_token: token } : {};
    return httpClient
      .post(API_URL + "/authentication/check_admin_token", qs.stringify(payload))
      .then((response) => response.data);
  }

  static resetPassword(email: string): Promise<ApiResponse> {
    return httpClient
      .post(
        API_URL + "/authentication/reset_password",
        qs.stringify({
          username: email,
        })
      )
      .then((response) => response.data);
  }

  static emailInformation(code: string): Promise<ApiResponse> {
    return httpClient
      .post(API_URL + "/authentication/email_information", qs.stringify({ code }))
      .then((response) => response.data);
  }

  static checkResetToken(token: string): Promise<ApiResponse> {
    return httpClient
      .post(
        API_URL + "/authentication/check_reset_token",
        qs.stringify({
          password_reset_token: token,
        })
      )
      .then((response) => response.data);
  }

  static setNewPassword(email: string, password: string, token: string): Promise<ApiResponse> {
    return httpClient
      .post(
        API_URL + "/authentication/set_new_password",
        qs.stringify({
          username: email,
          password,
          password_reset_token: token,
        })
      )
      .then((response) => response.data);
  }

  static updateProfile(data: UpdateProfilePayload): Promise<ApiResponse> {
    return httpClient
      .post(
        API_URL + "/authentication/update_profile",
        qs.stringify({
          first_name: data.first_name,
          last_name: data.last_name,
          password: data.password,
        })
      )
      .then((response) => response.data);
  }

  static updateParameters(data: UpdateParametersPayload): Promise<ApiResponse> {
    return httpClient
      .post(
        API_URL + "/authentication/update_parameters",
        qs.stringify({
          allow_email: data.allow_email,
          allow_notification: data.allow_notification,
          display_notification: data.display_notification,
          language: data.language,
          password: data.password,
          passwordConfirm: data.passwordConfirm,
        })
      )
      .then((response) => response.data);
  }

  static changeLicenceAcceptance(value: boolean | number): Promise<ApiResponse> {
    return httpClient
      .post(
        API_URL + "/authentication/change_eula_acceptance",
        qs.stringify({
          has_accepted: value,
        })
      )
      .then((response) => response.data);
  }

  static logout(): Promise<ApiResponse> {
    return httpClient.post(API_URL + "/logout").then((response) => response.data);
  }
}
